// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import type { Session } from './session.ts';
import type { Account } from './account';
import type { Server } from './server.ts';
import type { Channel } from './channel.ts';
import type { Member } from '@/models/member.ts';
import type { TwoFactorConfirmation } from '@/models/token.ts';
import type { UserSettings } from '@/models/user-settings.ts';
import type { LoginSession } from '@/models/login-session.ts';
import type { Device } from '@/models/device.ts';
import type { Conversation, DirectMessage } from '@/models';
import { AvatarPack } from '@/models/avatar-pack.ts';
import { Friend } from '@/models/friend.ts';
import { FriendRequest } from '@/models/friend-request.ts';
import { FriendInviteCode } from '@/models/friend-invite-code.ts';

export enum UserPlan {
  FREE = 'FREE',
  BASIC = 'BASIC',
  OPTIMIM = 'OPTIMIM', // typo, need OPTIMUM
  EXTENDED = 'EXTENDED',
}

export interface User {
  id: string;
  name?: string;
  nickname?: string;
  email?: string;
  emailVerified?: Date;
  image?: string;
  avatarPackId?: string;
  avatarPack?: AvatarPack;
  password?: string;
  accounts: Account[];
  sessions: Session[];
  isTwoFactorEnabled: boolean;
  twoFactorConfirmation?: TwoFactorConfirmation;
  phone?: string;
  phoneVerified?: Date;
  servers: Server[];
  members: Member[];
  channels: Channel[];
  plan: UserPlan;
  planExpiresAt?: Date;
  settings?: UserSettings;
  loginSessions: LoginSession[];
  device: Device[];
  friends: Friend[];
  friendOf: Friend[];
  inviteCodes: FriendInviteCode[];
  sentFriendRequests: FriendRequest[];
  recievedFriendRequests: FriendRequest[];
  currentGame?: string;
  lastActive?: Date;
  directMessages: DirectMessage[];
  conversationsInitiated: Conversation[];
  conversationsReceived: Conversation[];
  isBanned?: boolean; // fake field, not exist in DB, but generated by API
  createdAt: Date;
  updatedAt: Date;
}
